import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CustomProvider } from 'rsuite';
import esES from 'rsuite/locales/es_ES';
import { AuthProvider, useAuth } from './context/AuthContext';
import { configuracionAPI } from './services/api';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import CotizacionesList from './components/CotizacionesList';
import CotizacionForm from './components/CotizacionForm';
import PlantillasList from './components/PlantillasList';
import PlantillaForm from './components/PlantillaForm';
import ClientesList from './components/ClientesList';
import ClientesCotizaciones from './components/ClientesCotizaciones';
import ClienteCotizacionesDetalle from './components/ClienteCotizacionesDetalle';
import MisCotizaciones from './components/MisCotizaciones';
import ServiciosPanel from './components/ServiciosPanel';
import PersonalPanel from './components/PersonalPanel';
import CostosFijosPanel from './components/CostosFijosPanel';
import ConfiguracionPanel from './components/ConfiguracionPanel';
import UsuariosPanel from './components/UsuariosPanel';
import RolesPanel from './components/RolesPanel';
import AlertasPanel from './components/AlertasPanel';
import Dashboard from './components/Dashboard';
import Perfil from './components/Perfil';
import './galactic-quote.css';

// Wrapper para CotizacionForm con navegación
function CotizacionFormWrapper({ configuracion, onConfigUpdate, isNew }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const tipoCambio = parseFloat(configuracion.tipo_cambio) || 3.70;
    const igvRate = parseFloat(configuracion.igv_rate) || 0.18;
    const rentabilidad = configuracion.rentabilidad !== undefined && configuracion.rentabilidad !== null
        ? parseFloat(configuracion.rentabilidad)
        : 0.68;
    const rentabilidadCostoFijo = configuracion.rentabilidad_costo_fijo !== undefined && configuracion.rentabilidad_costo_fijo !== null
        ? parseFloat(configuracion.rentabilidad_costo_fijo)
        : 0.68;

    return (
        <CotizacionForm
            cotizacionId={isNew ? null : id}
            tipoCambio={tipoCambio}
            igvRate={igvRate}
            rentabilidad={rentabilidad}
            rentabilidadCostoFijo={rentabilidadCostoFijo}
            configuracion={configuracion}
            onSaved={() => navigate('/cotizaciones')}
            onCancel={() => navigate('/cotizaciones')}
        />
    );
}

// Wrapper para CotizacionesList con navegación
function CotizacionesListWrapper({ configuracion }) {
    const navigate = useNavigate();

    return (
        <CotizacionesList
            onEdit={(id) => navigate(`/cotizaciones/${id}`)}
            onNueva={() => navigate('/cotizaciones/nueva')}
            configuracion={configuracion}
        />
    );
}

// Protección de rutas por permiso (acepta string o array con lógica OR)
function ProtectedRoute({ permiso, children }) {
    const { tienePermiso } = useAuth();
    const permisos = Array.isArray(permiso) ? permiso : [permiso];
    const tieneAlguno = permisos.some(p => tienePermiso(p));
    if (!tieneAlguno) {
        return (
            <div className="card">
                <div className="card-header"><h2>Acceso denegado</h2></div>
                <div className="admin-content">
                    <p className="empty-message">No tienes permisos para acceder a esta sección.</p>
                </div>
            </div>
        );
    }
    return children;
}

// Componente principal de la aplicacion
function AppContent() {
    const { isAuthenticated, loading: authLoading } = useAuth();

    // Estados
    const [configuracion, setConfiguracion] = useState({});
    const [loading, setLoading] = useState(true);
    const [sidebarExpanded, setSidebarExpanded] = useState(window.innerWidth >= 1024);
    const handleSidebarExpanded = useCallback((val) => setSidebarExpanded(val), []);

    // Cargar configuracion al autenticarse
    useEffect(() => {
        if (isAuthenticated) {
            cargarConfiguracion();
        }
    }, [isAuthenticated]);

    const cargarConfiguracion = async () => {
        try {
            const response = await configuracionAPI.getAll();
            setConfiguracion(response.data || {});
        } catch (err) {
            console.error('Error al cargar configuracion:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleConfigUpdate = (newConfig) => {
        setConfiguracion(newConfig);
    };

    // Si esta cargando autenticacion
    if (authLoading) {
        return (
            <div className="galaxy">
                <div className="stars" />
                <div className="nebula" />
                <div className="loading-container">
                    <div className="loading">Cargando...</div>
                </div>
            </div>
        );
    }

    // Si no esta autenticado, mostrar login
    if (!isAuthenticated) {
        return <Login />;
    }

    return (
        <div className="galaxy">
            <Sidebar configuracion={configuracion} expanded={sidebarExpanded} onExpandedChange={handleSidebarExpanded} />
            <div className={`app-main-wrapper ${sidebarExpanded ? '' : 'app-main-wrapper--collapsed'}`}>
                <main className="main-content">
                    {loading ? (
                        <div className="loading">Cargando configuracion...</div>
                    ) : (
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route
                                path="/cotizaciones"
                                element={<ProtectedRoute permiso="gestion_cotizaciones"><CotizacionesListWrapper configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/cotizaciones/nueva"
                                element={
                                    <ProtectedRoute permiso="gestion_cotizaciones">
                                        <CotizacionFormWrapper
                                            configuracion={configuracion}
                                            onConfigUpdate={handleConfigUpdate}
                                            isNew={true}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/cotizaciones/clientes"
                                element={<ProtectedRoute permiso="gestion_cotizaciones"><ClientesCotizaciones /></ProtectedRoute>}
                            />
                            <Route
                                path="/cotizaciones/cliente/:id"
                                element={<ProtectedRoute permiso={["gestion_cotizaciones", "cotizaciones_asignadas"]}><ClienteCotizacionesDetalle configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/cotizaciones/mis-cotizaciones"
                                element={<MisCotizaciones configuracion={configuracion} />}
                            />
                            <Route
                                path="/cotizaciones/:id"
                                element={
                                    <ProtectedRoute permiso={["gestion_cotizaciones", "cotizaciones_asignadas"]}>
                                        <CotizacionFormWrapper
                                            configuracion={configuracion}
                                            onConfigUpdate={handleConfigUpdate}
                                            isNew={false}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/plantillas"
                                element={<ProtectedRoute permiso="gestion_plantillas"><PlantillasList /></ProtectedRoute>}
                            />
                            <Route
                                path="/plantillas/nueva"
                                element={<ProtectedRoute permiso="gestion_plantillas"><PlantillaForm configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/plantillas/:id"
                                element={<ProtectedRoute permiso="gestion_plantillas"><PlantillaForm configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/clientes"
                                element={<ProtectedRoute permiso="gestion_clientes"><ClientesList /></ProtectedRoute>}
                            />
                            <Route
                                path="/perfil"
                                element={<Perfil />}
                            />
                            <Route
                                path="/alertas"
                                element={
                                    <ProtectedRoute permiso="seguimientos">
                                        <div className="card">
                                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                                <h2>Alertas y Seguimiento</h2>
                                            </div>
                                            <AlertasPanel />
                                        </div>
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/servicios" element={<ProtectedRoute permiso="gestion_servicios"><ServiciosPanel /></ProtectedRoute>} />
                            <Route path="/personal" element={<ProtectedRoute permiso="gestion_personal"><PersonalPanel /></ProtectedRoute>} />
                            <Route path="/costos-fijos" element={<ProtectedRoute permiso="gestion_costos_fijos"><CostosFijosPanel /></ProtectedRoute>} />
                            <Route
                                path="/configuracion"
                                element={<ProtectedRoute permiso="configuracion"><ConfiguracionPanel onConfigUpdate={handleConfigUpdate} /></ProtectedRoute>}
                            />
                            <Route path="/usuarios" element={<ProtectedRoute permiso="gestion_usuarios"><UsuariosPanel /></ProtectedRoute>} />
                            <Route path="/roles" element={<ProtectedRoute permiso="gestion_roles"><RolesPanel /></ProtectedRoute>} />
                            <Route path="*" element={<Navigate to="/cotizaciones" replace />} />
                        </Routes>
                    )}
                </main>

                <footer className="end">
                    <div className="endGlow" />
                    <p className="endText">
                        {configuracion.empresa_nombre || 'Alma Industria Creativa'} - Don Presupuesto
                    </p>
                    <p className="endCopyright">
                        &copy; {new Date().getFullYear()} - <a href="https://almaquinta.com/" target="_blank" rel="noopener noreferrer">Alma Quinta</a>
                    </p>
                </footer>
            </div>
        </div>
    );
}

// Wrapper con AuthProvider y BrowserRouter
export default function App() {
    return (
        <CustomProvider locale={esES}>
            <BrowserRouter>
                <AuthProvider>
                    <AppContent />
                    <Toaster
                        position="bottom-center"
                        toastOptions={{
                            duration: 3000,
                            style: {
                                background: '#1a1f3c',
                                color: '#fff',
                                border: '1px solid rgba(140, 220, 255, 0.25)',
                                borderRadius: '12px',
                            },
                            success: {
                                iconTheme: {
                                    primary: '#10b981',
                                    secondary: '#1a1f3c',
                                },
                            },
                            error: {
                                iconTheme: {
                                    primary: '#ef4444',
                                    secondary: '#1a1f3c',
                                },
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </CustomProvider>
    );
}
